/* global globalThis */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Sidebar from './components/Sidebar';
import MainEditor from './components/Editor';
import AIChat from './components/AIChat';
import TopNavbar from './components/TopNavbar';
import axiosClientPayload from '../../../api-client/AxiosClientPayload';
import { showToast } from '../../common/Toaster';
import './styles/Scriptly.css';


const SCRIPTLY_API_BASE_CC = process.env.REACT_APP_API_BASE_URL_CC;

const isRuleObject = (target) => {
  if (!target || typeof target !== 'object') return false;
  const keys = Object.keys(target).map(k => k.toLowerCase());
  return keys.some(k => k.includes('join') || k.includes('condition') || k.includes('script'));
};

const getRuleValue = (target, ...keys) => {
  for (const k of keys) {
    const foundKey = Object.keys(target).find(pk => pk.toLowerCase() === k.toLowerCase());
    if (foundKey) return target[foundKey];
  }
  return '-- None';
};

const formatRuleContent = (tableName, rule) =>
  `-- TARGET TABLE: ${tableName}\n\n` +
  `/* PRE-SCRIPT */\n${getRuleValue(rule, 'pre_script', 'preScript')}\n\n` +
  `/* JOIN CONDITION */\n${getRuleValue(rule, 'join_condition', 'joinCondition')}\n\n` +
  `/* WHERE CONDITION */\n${getRuleValue(rule, 'where_condition', 'whereCondition')}\n\n` +
  `/* COLUMN TRANSFORMATIONS */\n${getRuleValue(rule, 'column_condition', 'columnCondition', 'column_transformations')}`;

const processRuleEntry = (key, value, parsedRules, recurse) => {
  const childrenKeys = Object.keys(value);
  const firstChild = value[childrenKeys[0]];

  if (isRuleObject(firstChild)) {
    childrenKeys.forEach(tableName => {
      parsedRules.push({ name: tableName, content: formatRuleContent(tableName, value[tableName]) });
    });
  } else if (isRuleObject(value)) {
    parsedRules.push({ name: key, content: formatRuleContent(key, value) });
  } else {
    recurse(value);
  }
};



const extractDocs = (data) => {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  if (Array.isArray(data.mapping_documents)) return data.mapping_documents;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.Documents)) return data.Documents;

  if (typeof data === 'string') {
    try {
      const p = JSON.parse(data);
      if (Array.isArray(p)) return p;
      if (p.data && Array.isArray(p.data)) return p.data;
      if (p.result && Array.isArray(p.result)) return p.result;
    } catch (e) {
      console.warn("Scriptly parse error:", e);
    }
  }
  return [];
};

const GenerateScript = () => {
  const [scLayer, setScLayer] = useState('Transform');
  const [scScriptType, setScScriptType] = useState('DDL');
  const [scType, setScType] = useState('SQL');
  const [scDefaultAction, setScDefaultAction] = useState('Create');
  const [scSafeScripts, setScSafeScripts] = useState(false);
  const [scQuoteOption, setScQuoteOption] = useState('Backticks');

  const [scSelectedObjects] = useState(new Set());
  const [scGeneratedSQL, setScGeneratedSQL] = useState(null);
  const [scGeneratedScripts, setScGeneratedScripts] = useState([]);
  const [scTransformationRules, setScTransformationRules] = useState([]);
  const [scReviewSummary, setScReviewSummary] = useState(null);
  const [scGenerating, setScGenerating] = useState(false);

  const [scMappingDocs, setScMappingDocs] = useState([]);
  const [scMappingDocsLoading, setScMappingDocsLoading] = useState(false);
  const [scSelectedMappings, setScSelectedMappings] = useState([]);

  const [scAiSidebarWidth, setScAiSidebarWidth] = useState(300);
  const [isResizingAi, setIsResizingAi] = useState(false);

  const startAiResizing = useCallback((e) => {
    setIsResizingAi(true);
    e.preventDefault();
  }, []);

  const stopAiResizing = useCallback(() => {
    setIsResizingAi(false);
  }, []);

  const doAiResizing = useCallback((e) => {
    if (isResizingAi) {
      const newWidth = globalThis.innerWidth - e.clientX;
      if (newWidth > 250 && newWidth < 800) {
        setScAiSidebarWidth(newWidth);
      }
    }
  }, [isResizingAi]);

  useEffect(() => {
    if (isResizingAi) {
      globalThis.addEventListener('mousemove', doAiResizing);
      globalThis.addEventListener('mouseup', stopAiResizing);
    } else {
      globalThis.removeEventListener('mousemove', doAiResizing);
      globalThis.removeEventListener('mouseup', stopAiResizing);
    }
    return () => {
      globalThis.removeEventListener('mousemove', doAiResizing);
      globalThis.removeEventListener('mouseup', stopAiResizing);
    };
  }, [isResizingAi, doAiResizing, stopAiResizing]);

  // Query State
  const [scQueryRunning, setScQueryRunning] = useState(false);
  const [scQueryResult, setScQueryResult] = useState(null);

  // AI State
  const [scAiInput, setScAiInput] = useState('');
  const [scAiMessages, setScAiMessages] = useState([]);
  const [scAiLoading, setScAiLoading] = useState(false);

  // UI State
  const [activeScriptTab, setActiveScriptTab] = useState(null);
  const [scViewMode, setScViewMode] = useState('code');

  // Table Fields State
  const [scTableFields, setScTableFields] = useState(null);

  const [extractInstance, setExtractInstance] = useState('Loading...');
  const [transformInstance, setTransformInstance] = useState('Loading...');

  useEffect(() => {
    const fetchEtlConfig = async () => {
      try {
        const organizationConfigId = localStorage.getItem("organization_config_id");
        if (!organizationConfigId) return;

        const response = await axiosClientPayload.get(
          `${SCRIPTLY_API_BASE_CC}/api/etl-configuration?organizationConfigId=${organizationConfigId}`
        );

        if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
          const extractConfig = response.data.data.find(c => c.etl_type === 'extract');
          const transformConfig = response.data.data.find(c => c.etl_type === 'transform');

          setExtractInstance(extractConfig ? extractConfig.instance_name : 'Not Configured');
          setTransformInstance(transformConfig ? transformConfig.instance_name : 'Not Configured');
        } else {
          setExtractInstance('Not Configured');
          setTransformInstance('Not Configured');
        }
      } catch (err) {
        console.error("Failed to fetch ETL configuration:", err);
        setExtractInstance('Error');
        setTransformInstance('Error');
      }
    };

    fetchEtlConfig();
  }, []);

  const fetchMappingDocs = useCallback(async () => {
    setScMappingDocsLoading(true);
    try {
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/mapping-documents`,
        { organization_config_id: organizationConfigId }
      );

      setScMappingDocs(extractDocs(response.data));
    } catch (err) {
      console.error("Failed to fetch mapping documents:", err);
    } finally {
      setScMappingDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappingDocs();
  }, [fetchMappingDocs]);

  useEffect(() => {
    const fetchTableFields = async () => {
      if (!scSelectedMappings || scSelectedMappings.length === 0) {
        setScTableFields(null);
        return;
      }
      try {
        const organizationConfigId = localStorage.getItem("organization_config_id") || "";
        const token = localStorage.getItem("token") || "";
        const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

        const payload = {
          organization_config_id: organizationConfigId,
          mapping_file_id: mappingFileIds,
          token: token
        };

        const response = await axiosClientPayload.post(
          `${SCRIPTLY_API_BASE_CC}/api/get-table-fields`,
          payload
        );

        setScTableFields(response.data);
      } catch (error) {
        console.error("Failed to fetch table fields:", error);
      }
    };
    fetchTableFields();
  }, [scSelectedMappings]);

  const handleGenerateSQL = useCallback(async () => {
    if (!scSelectedMappings || scSelectedMappings.length === 0) {
      showToast("Selection Required", "Please select at least one mapping document to generate a script.", "warn");
      return;
    }

    setScGenerating(true);
    try {
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const token = localStorage.getItem("token") || "";
      const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

      const payload = {
        token: token,
        organization_config_id: organizationConfigId,
        mapping_file_id: mappingFileIds,
        script_type: scScriptType,
        operation: scLayer,
        language: scType,
        session_id: ""
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/generate-script`,
        payload
      );

      // Parse response to extract scripts
      const scriptsObj = response.data?.scripts || {};
      const parsedScripts = [];

      Object.keys(scriptsObj).forEach(mappingId => {
        const types = scriptsObj[mappingId];
        Object.keys(types).forEach(typeKey => {
          const scriptsMap = types[typeKey];
          Object.keys(scriptsMap).forEach(scriptName => {
            parsedScripts.push({
              name: scriptName,
              content: scriptsMap[scriptName]
            });
          });
        });
      });


      const rulesObj = response.data?.transformation_rules || {};
      const parsedRules = [];

      const discoverRules = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        if (isRuleObject(obj)) return;
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value && typeof value === 'object') {
            processRuleEntry(key, value, parsedRules, discoverRules);
          }
        });
      };

      discoverRules(rulesObj);

      setScReviewSummary(response.data?.review_summary || null);
      setScGeneratedScripts(parsedScripts);
      setScTransformationRules(parsedRules);

      // Auto-switch mode if only rules are available
      if (parsedRules.length > 0 && parsedScripts.length === 0) {
        setScViewMode('rules');
      }

      if (parsedScripts.length > 0) {
        setScGeneratedSQL(parsedScripts[0].content);
      } else if (parsedRules.length > 0) {
        setScGeneratedSQL(parsedRules[0].content);
      } else {
        const msg = "-- No scripts returned from API.";
        setScGeneratedSQL(msg);
      }
    } catch (error) {
      console.error("Failed to generate script:", error);
      const errorMsg = error.response?.data?.message || error.message;
      setScGeneratedSQL(`-- Error generating script: ${errorMsg}`);
    } finally {
      setScGenerating(false);
    }
  }, [scSelectedMappings, scScriptType, scLayer, scType]);

  const activeScriptCode = useMemo(() => {
    const list = scViewMode === 'code' ? scGeneratedScripts : scTransformationRules;
    if (!list || list.length === 0) return scGeneratedSQL || "";
    const found = list.find(s => s.name === activeScriptTab);
    return found ? found.content : list[0].content;
  }, [activeScriptTab, scGeneratedScripts, scTransformationRules, scGeneratedSQL, scViewMode]);

  const handleRunQuery = useCallback(() => {
    setScQueryRunning(true);
    setTimeout(() => {
      setScQueryResult({
        cols: ['id', 'name', 'status', 'created_at'],
        rows: [
          ['1', 'Alice Johnson', 'active', '2025-01-10 09:23:00'],
          ['2', 'Bob Smith', 'inactive', '2025-01-12 14:05:00'],
          ['3', 'Carol Williams', 'active', '2025-02-03 11:42:00'],
        ],
      });
      setScQueryRunning(false);
    }, 800);
  }, []);

  const handleAiAlter = useCallback(async (prompt) => {
    if (!prompt.trim()) return;
    setScAiLoading(true);
    setScAiMessages(m => [...m, { role: 'user', text: prompt }]);
    setScAiInput('');

    try {
      const payload = {
        code: activeScriptCode,
        query: prompt,
        language: scType,
        mode: "edit",
        conversation_history: null
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/ai-assist`,
        payload
      );

      const updatedCode = response.data?.modified_code;
      const explanation = response.data?.explanation;
      const statusMessage = response.data?.message;

      if (updatedCode || statusMessage) {
        if (updatedCode) {
          if (scViewMode === 'rules') {
            setScTransformationRules(prev => prev.map(s =>
              s.name === activeScriptTab ? { ...s, content: updatedCode } : s
            ));
          } else if (scGeneratedScripts.length > 0 && activeScriptTab) {
            setScGeneratedScripts(prev => prev.map(s =>
              s.name === activeScriptTab ? { ...s, content: updatedCode } : s
            ));
          } else {
            setScGeneratedSQL(updatedCode);
          }
        }

        setScAiMessages(m => [...m, {
          role: 'ai',
          text: statusMessage || "Analysis complete.",
          explanation: Array.isArray(explanation) ? explanation : null
        }]);
      } else {
        throw new Error("I processed your request but no changes were returned.");
      }
    } catch (error) {
      console.error("AI Assist error:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to process AI request";
      setScAiMessages(m => [...m, { role: 'ai', text: `Sorry, I encountered an error: ${errorMsg}` }]);
    } finally {
      setScAiLoading(false);
    }
  }, [activeScriptCode, scType, activeScriptTab, scGeneratedScripts]);

  const handleFixWithAI = useCallback(async (tableName, issuePoint, scriptType) => {
    // Find the script content for this table
    const targetScript = scGeneratedScripts.find(s => s.name === tableName);
    const codeToFix = targetScript?.content || activeScriptCode || '';

    if (!codeToFix) {
      showToast('No Code', 'No script code found for this table to fix.', 'warn');
      return;
    }

    const fixQuery = `Fix the following issue in the ${tableName} ${scriptType || ''} script: ${issuePoint}`;

    // Show user message in AI Chat (same flow as normal AI assist)
    setScAiMessages(m => [...m, { role: 'user', text: fixQuery }]);
    setScAiInput('');
    setScAiLoading(true);

    try {
      const payload = {
        code: codeToFix,
        query: fixQuery,
        language: scType,
        mode: 'edit',
        conversation_history: null
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/ai-assist`,
        payload
      );

      const updatedCode = response.data?.modified_code;
      const explanation = response.data?.explanation;
      const statusMessage = response.data?.message;

      if (updatedCode || statusMessage) {
        if (updatedCode) {
          // Update the specific script by table name
          if (targetScript) {
            setScGeneratedScripts(prev => prev.map(s =>
              s.name === tableName ? { ...s, content: updatedCode } : s
            ));
          } else {
            setScGeneratedSQL(updatedCode);
          }

          // Auto-switch to the fixed script tab
          setActiveScriptTab(tableName);
          setScViewMode('code');
        }

        // Show AI response in chat
        setScAiMessages(m => [...m, {
          role: 'ai',
          text: statusMessage || `Fix applied for ${tableName} successfully.`,
          explanation: Array.isArray(explanation) ? explanation : null
        }]);
      } else {
        setScAiMessages(m => [...m, {
          role: 'ai',
          text: 'I processed the request but no changes were returned.'
        }]);
      }
    } catch (error) {
      console.error('Fix with AI error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to apply AI fix';
      setScAiMessages(m => [...m, {
        role: 'ai',
        text: `Sorry, I encountered an error: ${errorMsg}`
      }]);
    } finally {
      setScAiLoading(false);
    }
  }, [scGeneratedScripts, activeScriptCode, scType]);

  return (
    <div className="scriptly-container flex flex-col h-screen bg-[#f8fafc] overflow-hidden relative">
      <TopNavbar
        extractInstance={extractInstance}
        transformInstance={transformInstance}
        handleGenerateSQL={handleGenerateSQL}
        scGenerating={scGenerating}
        scMappingDocs={scMappingDocs}
        scMappingDocsLoading={scMappingDocsLoading}
        scSelectedMappings={scSelectedMappings}
        setScSelectedMappings={setScSelectedMappings}
      />

      <div className="scriptly-main-content flex-1 flex overflow-hidden relative">
        <Sidebar
          scLayer={scLayer} setScLayer={setScLayer}
          scScriptType={scScriptType} setScScriptType={setScScriptType}
          scType={scType} setScType={setScType}
          scDefaultAction={scDefaultAction} setScDefaultAction={setScDefaultAction}
          scSafeScripts={scSafeScripts} setScSafeScripts={setScSafeScripts}
          scQuoteOption={scQuoteOption} setScQuoteOption={setScQuoteOption}
        />

        <MainEditor
          extractInstance={extractInstance}
          transformInstance={transformInstance}
          scLayer={scLayer}
          scTableFields={scTableFields}
          scSelectedObjects={scSelectedObjects}
          scGeneratedSQL={scGeneratedSQL}
          scGeneratedScripts={scGeneratedScripts}
          scTransformationRules={scTransformationRules}
          activeScriptTab={activeScriptTab}
          setActiveScriptTab={setActiveScriptTab}
          scViewMode={scViewMode}
          setScViewMode={setScViewMode}
          scReviewSummary={scReviewSummary}
          scType={scType}
          scGenerating={scGenerating}
          handleRunQuery={handleRunQuery}
          scQueryResult={scQueryResult}
          scQueryRunning={scQueryRunning}
          scMappingDocs={scMappingDocs}
          onFixWithAI={handleFixWithAI}
        />

        <button
          type="button"
          className="ai-resizer"
          aria-label="Resize AI sidebar"
          onMouseDown={startAiResizing}
        />

        <div style={{ width: scAiSidebarWidth, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          <AIChat
            scAiInput={scAiInput} setScAiInput={setScAiInput}
            scAiMessages={scAiMessages} setScAiMessages={setScAiMessages}
            scAiLoading={scAiLoading}
            handleAiAlter={handleAiAlter}
          />
        </div>

        {scGenerating && (
          <div className="generation-overlay">
            <div className="quantum-loader-card">
              <div className="loader-spinner-ring" />
              <div className="loader-header">
                <h3 className="loader-title">Generating your scripts...</h3>
                <p className="loader-subtext">This may take a few moments</p>
              </div>
            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default GenerateScript;